import { Controller } from '@hotwired/stimulus';
import { application } from 'src';
import {
  addClass,
  removeClass,
  find,
  findAll,
  enableElement,
  displayElement,
  disableElement,
  hideElement
} from '@fretadao/f-js-dom';

application.register(
  'line-schedule-requests-form-request-type',
  class extends Controller {
    static targets = [
      'requestType', 'reasonId', 'tripKind', 'timeShiftType',
      'firstTravelDate', 'lastTravelDate', 'targetTime', 'shiftDelta'
    ];

    LINES_BOX_COLUMNS = {
      officialSchedule: 'Horário oficial',
      newSchedule: 'Novo horário',
      daysToChange: 'Dia(s) da alteração',
      ogOfficialSchedule: 'Horário oficial - ida',
      outgoingOldSchedule: 'Horário oficial - volta',
      newDestinationEta: `
        <span class="tooltip rounded shadow py-1 px-2 bg-gray-700 text-white -mt-8">
          Este horário é uma estimativa e pode mudar
        </span>
        Horário estimado - ida <i class="fa fa-info-circle"></i>
      `,
      outgoingNewSchedule: `
        <span class="tooltip rounded shadow py-1 px-2 bg-gray-700 text-white -mt-8">
          Este horário é uma estimativa e pode mudar
        </span>
        Horário estimado - volta <i class="fa fa-info-circle"></i>
      `
    };

    connect() {
      this.changedRequestType();
    }

    changedRequestType() {
      const requestType = this.hasRequestTypeTarget ? this.requestTypeTarget.value : null;
      const fieldsets = findAll('#line-schedule-change-request-form form fieldset');
      const targetFieldset = find(`#line-schedule-change-request-form form fieldset#${requestType}`);

      if (targetFieldset) {
        fieldsets.forEach((fieldset) => {
          [disableElement, hideElement].forEach((elementFunction) => elementFunction(fieldset));
        });
        enableElement(targetFieldset);
        targetFieldset.removeAttribute('style');
      }

      switch (requestType) {
        case 'lines_run':
        case 'lines_not_run':
          // the following line must be removed when activating the destination change request
          // as it manages the fields through other means
          this.#hideTimeChangeFields();
          this.#adjustSelectedLinesBoxForRunOrNotRunLines();
          break;
        case 'destination_change':
          this.#adjustSelectedLinesBoxForDestinationChange();
          break;
        default:
          this.#showTimeChangeFields();
          this.#adjustSelectedLinesBoxForTimeChange();
          break;
      }

      this.element.dispatchEvent(new Event('enable-submit:check'));
    }

    #hideTimeChangeFields() {
      this.#hideContainer(this.reasonIdTarget);
      this.#hideContainer(this.tripKindTarget, 'ingoing');
      this.#hideContainer(this.timeShiftTypeTarget);
      this.#hideContainer(this.targetTimeTarget);
      this.#hideContainer(this.shiftDeltaTarget);
    }

    #showTimeChangeFields() {
      this.#showContainer(this.reasonIdTarget);
      this.#showContainer(this.tripKindTarget);
      this.#showContainer(this.timeShiftTypeTarget);
      this.#showContainer(this.targetTimeTarget);
    }

    #hideContainer(container, defaultValue) {
      addClass(container, 'hidden');
      const field = find('select,input', container);

      field.required = false;
      field.value = defaultValue;
    }

    #showContainer(container) {
      removeClass(container, 'hidden');
      const field = find('select,input', container)

      field.required = true;
    }

    #adjustSelectedLinesBoxForRunOrNotRunLines() {
      const columns = Array.from(findAll('th', this.selectedLinesBoxTarget));
      const officialSchedule = columns.find((th) => th.textContent === this.LINES_BOX_COLUMNS.officialSchedule);
      const ogOfficialSchedule = columns.find((th) => th.textContent == this.LINES_BOX_COLUMNS.ogOfficialSchedule);
      const newDestinationEta = columns.find((th) => th.innerHTML == this.LINES_BOX_COLUMNS.newDestinationEta);
      const outgoingOldSchedule = columns.find((th) => th.textContent == this.LINES_BOX_COLUMNS.outgoingOldSchedule);
      const outgoingNewSchedule = columns.find((th) => th.innerHTML == this.LINES_BOX_COLUMNS.outgoingNewSchedule);

      if (officialSchedule) {
        officialSchedule.textContent = this.LINES_BOX_COLUMNS.daysToChange;
        addClass(columns.find((th) => th.textContent === this.LINES_BOX_COLUMNS.newSchedule), 'hidden');
      } else if (ogOfficialSchedule && newDestinationEta && outgoingOldSchedule && outgoingNewSchedule) {
        ogOfficialSchedule.textContent = this.LINES_BOX_COLUMNS.daysToChange;
        newDestinationEta.textContent = this.LINES_BOX_COLUMNS.newSchedule;

        addClass(newDestinationEta, 'hidden');

        outgoingOldSchedule.remove();
        outgoingNewSchedule.remove();
      }
    }

    #adjustSelectedLinesBoxForTimeChange() {
      const columns = Array.from(findAll('th', this.selectedLinesBoxTarget));
      const daysToChange = columns.find((th) => th.textContent === this.LINES_BOX_COLUMNS.daysToChange);
      const ogOfficialSchedule = columns.find((th) => th.textContent == this.LINES_BOX_COLUMNS.ogOfficialSchedule);
      const newDestinationEta = columns.find((th) => th.innerHTML == this.LINES_BOX_COLUMNS.newDestinationEta);
      const outgoingOldSchedule = columns.find((th) => th.textContent == this.LINES_BOX_COLUMNS.outgoingOldSchedule);
      const outgoingNewSchedule = columns.find((th) => th.innerHTML == this.LINES_BOX_COLUMNS.outgoingNewSchedule);

      if (daysToChange) {
        daysToChange.textContent = this.LINES_BOX_COLUMNS.officialSchedule;
        removeClass(columns.find((th) => th.textContent === this.LINES_BOX_COLUMNS.newSchedule), 'hidden');
      } else if (ogOfficialSchedule && newDestinationEta && outgoingOldSchedule && outgoingNewSchedule) {
        ogOfficialSchedule.textContent = this.LINES_BOX_COLUMNS.officialSchedule;
        newDestinationEta.textContent = this.LINES_BOX_COLUMNS.newSchedule;

        outgoingOldSchedule.remove();
        outgoingNewSchedule.remove();
      }
    }

    #adjustSelectedLinesBoxForDestinationChange() {
      const tableHeads = findAll('thead', this.selectedLinesBoxTarget);
      const columns = Array.from(findAll('th', this.selectedLinesBoxTarget));
      const officialSchedule = columns.find((th) => th.textContent === this.LINES_BOX_COLUMNS.officialSchedule);
      const newSchedule = columns.find((th) => th.textContent === this.LINES_BOX_COLUMNS.newSchedule);
      const daysToChange = columns.find((th) => th.textContent === this.LINES_BOX_COLUMNS.daysToChange);
      const classes = find('th', this.selectedLinesBoxTarget).classList;

      if ((officialSchedule && newSchedule) || (daysToChange && newSchedule)) {
        if (officialSchedule && newSchedule) {
          officialSchedule.textContent = this.LINES_BOX_COLUMNS.ogOfficialSchedule;
        } else if (daysToChange && newSchedule) {
          daysToChange.textContent = this.LINES_BOX_COLUMNS.ogOfficialSchedule;
          removeClass(newSchedule, 'hidden');
        }

        newSchedule.textContent = this.LINES_BOX_COLUMNS.newDestinationEta;
        tableHeads.forEach((thead) => {
          const outgoingOldSchedule = document.createElement('th');
          const outgoingNewSchedule = document.createElement('th');
          const row = find('tr', thead);

          outgoingOldSchedule.textContent = this.LINES_BOX_COLUMNS.outgoingOldSchedule;
          outgoingNewSchedule.textContent = this.LINES_BOX_COLUMNS.outgoingNewSchedule;
          outgoingOldSchedule.classList = classes;
          outgoingNewSchedule.classList = classes;
          outgoingNewSchedule.classList.add('has-tooltip');
          outgoingNewSchedule.dataset.role = 'outgoingNewScheduleCell';
          outgoingNewSchedule.innerHTML = this.LINES_BOX_COLUMNS.outgoingNewSchedule;

          row.appendChild(outgoingOldSchedule);
          row.appendChild(outgoingNewSchedule);
        });

        newSchedule.classList.add('has-tooltip');
        newSchedule.dataset.role = 'newScheduleCell';
        newSchedule.innerHTML = this.LINES_BOX_COLUMNS.newDestinationEta;
      }
    }
  }
);
